
import React, { useCallback, useEffect, useState } from 'react';
import app, { api, use } from 'xadmin';
import { lazy } from 'xadmin-ui';
import { _t } from 'xadmin-i18n'

import BatchChange from './BatchChange'
import ModelContext from './context';
import effects from './effects';
import mappers from './mappers';
import models from './models';
import reducers from './reducers';
import BatchExportTags from './BatchExportTags'

const ModelsPage = lazy(() => import('./pages/Models'))
const Nodes = lazy(() => import('./pages/Nodes'))
const ChangeNotes = lazy(() => import('./pages/ChangeNotes'))
const AssetsNotes = lazy(() => import('./pages/AssetsNotes'))
const InitNodeDash = lazy(() => import('./pages/InitNodeDash'))
const InitFrontNodeDash = lazy(() => import('./pages/InitFrontNodeDash'))
import { DataForm } from './models'

const getFieldProp = (model, field) => {
  return field.split('.').reduce((obj, f) => {
    return obj && obj.properties && obj.properties[f]
  }, model)
}

export default {
  modelBatchActions: {
    edit: {
      default: true,
      component: (props) => {
        const model = props.model
        if (!!model.permission && !!model.permission.edit) {
          return <BatchChange {...props} />
        } else {
          return null
        }
      }
    },
    export: {
      default: true,
      component: (props) => {
        const model = props?.model
        return model?.name == 'node' && <BatchExportTags {...props} />
      }
    }
  },
  frontMenuItems: [
    { title: '资产管理', url: '/app/nodes', icon: 'Node', permission: 'node.view' }
  ],
  menuItems: [
    { title: '模型管理', parent: 'scada', url: '/app/cmdb', icon: 'Cmdb', permission: 'model.view' },
    { title: '资产管理', parent: 'scada', url: '/app/nodes', icon: 'Node', permission: 'node.view' }
  ],
  routers: {
    '/app/': [
      {
        path: 'cmdb',
        component: ModelsPage
      },
      {
        path: 'nodes',
        component: Nodes
      },
      {
        path: 'changeNotes',
        component: ChangeNotes
      },
      {
        path: 'assetsNotes',
        component: AssetsNotes
      }
    ]
  },
  components: {
    'ModelContext.Provider': ModelContext.Provider,
    'ModelContext.Consumer': ModelContext.Consumer,
    InitNodeDash, InitFrontNodeDash, 
    'NodeTabs.Form': DataForm
  },
  filter_converter: [(f, schema, options) => {
    if (schema.fieldType == 'department' && schema.description) {
      f.description = null
    }
    return f
  }],
  hooks: {
    'model.get': props => {
      const { model, rest, id, query, item } = use('model', props)

      const [state, setState] = useState(() => {
        let data = item
        if (!data) {
          if (model.defaultValue) {
            data = _.isFunction(model.defaultValue) ? model.defaultValue() : model.defaultValue
          }
          if (!_.isEmpty(query)) {
            data = { ...data, ...query }
          }
        }
        return { data, loading: Boolean(!data && id) }
      })

      const loadData = (id, manual) => {
        const data = state?.data
        if ((!data && id) || (data && data.id != id) || manual) {
          setState({ data, loading: true })
          rest.get(id).then(payload => {
            setState({ data: payload, loading: false })
          })
        }
      }

      useEffect(() => {
        loadData(id)
      }, [id])

      const title = id ? _t('Edit {{title}}', { title: model.title }) : _t('Create {{title}}', { title: model.title })

      return { ...props, model, title, loadData, ...state }
    },
    'iot.cmdb.models': props => {
      const [{ models, loading }, set] = React.useState({ models: null, loading: true })
      React.useEffect(() => {
        api('Model').query().then(({ items }) => set({ models: items, loading: false }))
      }, [])
      return { ...props, models, loading }
    },
    'model.fields': props => {
      const { model, getModelState, modelDispatch } = use('model', props)

      const changeFieldDisplay = useCallback(([field, selected], lists) => {
        const filter = getModelState().filter
        let fields = [].concat(filter.fields || [])
        const index = _.indexOf(fields, field)
        let resetFields;

        if (lists?.length) {
          resetFields = lists?.length ? lists.filter(item => item[1]).map(item => item[0]) : []
          fields = resetFields
        } else {
          if (selected) {
            if (index === -1) fields.push(field)
          } else {
            _.remove(fields, (i) => { return i === field })
          }
          let list = Array.from(new Set([...model.listFields, ...Object.keys(model.properties)]))
          resetFields = list.filter(f => fields.indexOf(f) >= 0)
        }
        modelDispatch({
          type: 'GET_ITEMS', filter: { ...filter, fields: resetFields },
          payload: 'changeField'
        })
      }, [getModelState, modelDispatch, model])

      if (model.name == 'node') {
        const fields = Object.keys(model.properties).reduce((prev, name) => {
          let prop = model.properties[name]
          if (prop && prop.type == 'object' && name != 'model' && prop.properties) {
            Object.keys(prop.properties).forEach((n2) => {
              // 资产list表格过滤不需要显示字段
              if (prop.showInList !== false || name == 'custom') {
                prev[`${name}.${n2}`] = prop.properties[n2]
              }
            })
          } else {
            prev[name] = prop
          }
          return prev
        }, {})
        return { ...props, fields, changeFieldDisplay }
      }
      return { ...props, changeFieldDisplay }
    },
    'model.list.header': props => {
      const { field, model, modelState, modelDispatch } = use('model', props)

      const property = getFieldProp(model, field) || {}
      const canOrder = (property.canOrder !== undefined ? property.canOrder :
        (property.orderField !== undefined || (property.type != 'object' && property.type != 'array')))

      const changeOrder = React.useCallback((order) => {
        const filter = modelState.filter
        // 单个属性排序，初始值 orders 为空，每一次排序都会清空之前的排序
        // const orders = filter.order || {}
        const orders = {}
        const property = getFieldProp(model, field) || {}
        orders[property.orderField || field] = order

        modelDispatch({ type: 'GET_ITEMS', filter: { ...filter, order: orders } })
      }, [modelDispatch, modelState.filter])

      const { order } = use('model', props, state => {
        const orders = state.filter.order
        return { order: orders !== undefined ? (orders[property.orderField || field] || '') : '' }
      })

      const title = property.header || property.title || _.startCase(field)

      return { ...props, title, changeOrder, canOrder, order, property }
    },
  },
  reducers,
  effects,
  mappers,
  models
}
